import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { PeriodRow, SelectedClient } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { CompanyInfo } from '@/features/tr-reportsettings/CompanyInfo';
import { isUnincorporated, useClientKind } from '@/features/tr-reportsettings/clientKind';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyState } from '@/kit/EmptyState';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { Tooltip } from '@/kit/Tooltip';
import { Toggle } from '@/kit/Toggle';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { openDialog } from '@/kit/dialog';
import { AddAccountingPeriod, type AccountingPeriodDialogData } from './AddAccountingPeriod';
import './TaskTablePage.scss';

const pageSizeOptions = [10, 25, 50, 100];

/**
 * Angular `openReportOption()`: opens Company Info as a dialog. No template of the Accounting Periods screen calls it
 * (it was never wired to a control), so nothing in the React screen triggers it either; kept, as asked, so the call
 * site exists once a trigger is decided.
 */
export function openReportOption(): void {
  openDialog(CompanyInfo);
}

export const AUTO_GENERATION_EXPLANATION =
  'Checking this option will automatically generate a new accounting period once the end date of current accounting period is reached.';

/** Angular TaxreturnSettingsComponent: despite the name, the "Accounting Periods" task screen. */
export function TaxreturnSettings() {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<PeriodRow[]>([]);
  /** The first load has finished: only then is an empty list "empty" (not still loading). */
  const [loaded, setLoaded] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]!);
  /**
   * Angular had `[checked]="autoGeneration"` on a mat-checkbox: the box toggles itself on click, and the model only
   * pushes a value into it when the bound value CHANGES. So the model (`autoGenerationModel`) and what the box shows
   * (`autoGeneration`) can differ, e.g. after a failed save (see onAutoGenerationChange).
   */
  const [autoGeneration, setAutoGenerationView] = useState(false);
  const autoGenerationModel = useRef(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [autoGenAnchor, setAutoGenAnchor] = useState<HTMLDivElement | null>(null);

  const setAutoGeneration = (value: boolean) => {
    if (value !== autoGenerationModel.current) {
      setAutoGenerationView(value);
    }
    autoGenerationModel.current = value;
  };
  const clientRef = useRef<SelectedClient | null>(null);
  // BUG-078: a partnership or sole trader files no CT600 (a partnership's return is an SA800), so it has no CT600 due date.
  const noCt600 = isUnincorporated(useClientKind(selectedClient()?.id)?.clientType);

  const load = (suppressAutoGenerateMessage = false) => {
    const client = clientRef.current;
    if (!client) {
      return;
    }
    practiceService.getClientPeriods(client.id).then(
      (response) => {
        setDataSource(response.periods);
        setLoaded(true);
        setAutoGeneration(response.autoPeriodGeneration);
        if (!suppressAutoGenerateMessage && response.generatedPeriods > 0) {
          notifySuccess(NotificationTextMessage.autoPeriodGenerationMessage);
        }
      },
      (error) => {
        setLoaded(true);
        notifyError(error);
      },
    );
  };

  useEffect(() => {
    clientRef.current = selectedClient();
    if (!clientRef.current) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit: once
  }, []);

  const openPeriodDialog = (data: AccountingPeriodDialogData) => {
    openDialog(AddAccountingPeriod, { data })
      .afterClosed()
      .then((saved) => {
        if (saved) {
          notifySuccess('Accounting period saved successfully');
          load(true);
        }
      });
  };

  const clickAccountingPeriod = () => {
    const client = clientRef.current;
    if (!client) {
      return;
    }
    const isFirstPeriod = dataSource.length === 0;
    practiceService.getNextPeriod(client.id).then(
      (suggestion) => openPeriodDialog({ clientId: client.id, suggestion, isFirstPeriod }),
      (error) => notifyError(error),
    );
  };

  const clickPriorAccountingPeriod = () => {
    const client = clientRef.current;
    if (!client) {
      return;
    }
    practiceService.getPriorPeriod(client.id).then(
      (suggestion) => openPeriodDialog({ clientId: client.id, suggestion, isPrior: true, isFirstPeriod: false }),
      (error) => notifyError(error),
    );
  };

  const onEditClick = (row: PeriodRow) => {
    if (clientRef.current) {
      const isFirstPeriod = dataSource.length === 1 && dataSource[0].id === row.id;
      openPeriodDialog({ clientId: clientRef.current.id, period: row, isFirstPeriod });
    }
  };

  const openDeleteClick = (row: PeriodRow) => {
    openDialog(ClosePopup, { data: { itemName: 'Accounting Period', count: 1 } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        // Issue #33 (QA): with Auto Period Generation on, the reload after the delete would create the deleted period again.
        // So the page switches generation off itself, right after the delete and before the reload (the API now does the same
        // with the delete; asking again is harmless), and says so. It is ticked again to resume.
        const autoWasOn = autoGenerationModel.current;
        practiceService.deletePeriod(row.id).then(
          async () => {
            let switchedOff = false;
            const client = clientRef.current;
            if (autoWasOn && client) {
              try {
                await practiceService.setAutoPeriodGeneration(client.id, false);
                setAutoGeneration(false);
                setPopoverOpen(false);
                switchedOff = true;
              } catch (error) {
                notifyError(error);
              }
            }
            notifySuccess(
              switchedOff
                ? `${NotificationTextMessage.recordDeletedSuccessfully}. Auto Period Generation has been switched off.`
                : NotificationTextMessage.recordDeletedSuccessfully,
            );
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  // Return the settle promise (success or failure) so the row's action picker knows when to stop showing what was
  // just chosen and go back to its "Select" placeholder.
  const toggleLock = (row: PeriodRow) =>
    practiceService.lockPeriod(row.id, !row.isLocked).then(
      (updated) => {
        notifySuccess(
          updated.isLocked ? NotificationTextMessage.lockPeriodMessage : NotificationTextMessage.unlockPeriodMessage,
        );
        load();
      },
      (error) => notifyError(error),
    );

  const toggleCeased = (row: PeriodRow) =>
    practiceService.ceasePeriod(row.id, !row.isCeased).then(
      (updated) => {
        notifySuccess(
          updated.isCeased ? NotificationTextMessage.ceasedMessage : NotificationTextMessage.continueMessage,
        );
        load();
      },
      (error) => notifyError(error),
    );

  const toggleComparative = (row: PeriodRow) =>
    practiceService.markComparative(row.id, !row.isComparative).then(
      (updated) => {
        notifySuccess(
          updated.isComparative
            ? NotificationTextMessage.markComparativeMessage
            : NotificationTextMessage.undoComparativeMessage,
        );
        load();
      },
      (error) => notifyError(error),
    );

  const onAutoGenerationChange = (enabled: boolean) => {
    const client = clientRef.current;
    // The mat-checkbox flips itself as soon as it is clicked (the model is untouched until the server answers).
    setAutoGenerationView(enabled);
    if (enabled) {
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
    if (!client) {
      return;
    }
    practiceService.setAutoPeriodGeneration(client.id, enabled).then(
      () => {
        setAutoGeneration(enabled);
        if (enabled) {
          load();
        }
      },
      (error) => {
        // Angular set `autoGeneration = !enabled`. The bound value is usually unchanged (it still holds !enabled), so
        // the box keeps showing what the user clicked: it does NOT flip back.
        setAutoGeneration(!enabled);
        setPopoverOpen(false);
        notifyError(error);
      },
    );
  };

  // Issue #34 (QA): only the latest period (the one ending last, whichever page it is on) can be deleted; the others do not
  // offer Delete.
  const latestPeriodId = dataSource.reduce<PeriodRow | null>(
    (latest, p) => (latest === null || p.periodTo > latest.periodTo ? p : latest),
    null,
  )?.id;

  const columns: DataTableColumn<PeriodRow>[] = [
    { id: 'fromdate', header: 'From', cell: (element) => element.fromDate },
    { id: 'todate', header: 'To', cell: (element) => element.toDate },
    { id: 'aduedate', header: 'Accounts Due Date', cell: (element) => element.accountsDueDate },
    ...(noCt600
      ? []
      : [{ id: 'cduedate', header: 'CT600 Due Date', cell: (element: PeriodRow) => element.ct600DueDate }]),
    {
      id: 'status',
      header: 'Status',
      cell: (element) => <Icon name={element.isLocked ? 'lock' : 'lock_open'} outlined title={element.status} />,
    },
    {
      id: 'action',
      header: 'Action',
      cell: (element) => (
        <PeriodActionSelect
          element={element}
          canDelete={element.id === latestPeriodId}
          onEdit={onEditClick}
          onDelete={openDeleteClick}
          onLock={toggleLock}
          onCeased={toggleCeased}
          onComparative={toggleComparative}
        />
      ),
    },
  ];

  const maxPageIndex = Math.max(0, Math.ceil(dataSource.length / pageSize) - 1);
  const shownPageIndex = Math.min(pageIndex, maxPageIndex);
  const pageRows = dataSource.slice(shownPageIndex * pageSize, shownPageIndex * pageSize + pageSize);

  return (
    <SideMenuPage menu="settings" contentClassName="tr-table-page">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={clickAccountingPeriod}>
              <Icon name="add" />
              <span>Accounting Period</span>
            </Button>
            <Button className="action-button primary-button" onClick={clickPriorAccountingPeriod}>
              <Icon name="add" />
              <span>Prior Accounting Period</span>
            </Button>
          </div>

          <div ref={setAutoGenAnchor} className="content-header-right mb-1">
            <Tooltip title={AUTO_GENERATION_EXPLANATION}>
              {/* 2 Nov design review: a toggle, not a checkbox; it still saves at once through the same handler. The span carries the hover tooltip (Toggle takes no ref). */}
              <span className="d-inline-flex">
                <Toggle checked={autoGeneration} onCheckedChange={onAutoGenerationChange}>
                  Auto Period Generation
                </Toggle>
              </span>
            </Tooltip>
            {/* BUG-056: 3.0's explanation is a hover popover (Periods.js:254 `data-toggle="popover-hover"`) that never
                blocks the page. A modal Popover laid an invisible backdrop over the whole screen until it was clicked
                away, so the next click (the Dashboard menu, a period's Action) was swallowed. Non-modal: the page
                stays usable and a click anywhere else closes it. */}
            <Popper
              open={Boolean(popoverOpen && autoGenAnchor)}
              anchorEl={autoGenAnchor}
              placement="bottom-end"
              style={{ zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setPopoverOpen(false)}>
                <div
                  role="tooltip"
                  style={{
                    maxWidth: 340,
                    padding: '10px 14px',
                    fontSize: '13px',
                    lineHeight: 1.4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    color: '#333',
                  }}
                >
                  {AUTO_GENERATION_EXPLANATION}
                </div>
              </ClickAwayListener>
            </Popper>
          </div>
        </div>
        {/* The paginated height (TaskTablePage.scss) leaves the paginator on screen below the grid. */}
        <div className="mat-table-wrapper mat-table-wrapper-pagination">
          <DataTable
            columns={columns}
            rows={pageRows}
            rowKey={(row) => row.id}
            stickyHeader
            noDataMessage={
              loaded ? (
                <EmptyState
                  icon="date_range"
                  title="No accounting periods yet"
                  hint="Add the first accounting period to start working on this client."
                  action={
                    <Button className="action-button primary-button" onClick={clickAccountingPeriod}>
                      <Icon name="add" />
                      <span>Add the first period</span>
                    </Button>
                  }
                />
              ) : undefined
            }
          />
        </div>
        {/* Issue #6 (QA): "Items per page / 1 – 1 of 1" is noise on a short list. It shows once there are more rows than the
            smallest page size, which is when there is a page to move to or a page size worth changing. */}
        {dataSource.length > pageSizeOptions[0]! && (
          <Paginator
            length={dataSource.length}
            pageIndex={shownPageIndex}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPage={(event) => {
              setPageIndex(event.pageIndex);
              setPageSize(event.pageSize);
            }}
          />
        )}
      </div>
    </SideMenuPage>
  );
}

interface PeriodActionsProps {
  element: PeriodRow;
  /** Only the latest period is offered Delete (issue #34). */
  canDelete: boolean;
  onEdit: (row: PeriodRow) => void;
  onDelete: (row: PeriodRow) => void;
  onLock: (row: PeriodRow) => Promise<unknown>;
  onCeased: (row: PeriodRow) => Promise<unknown>;
  onComparative: (row: PeriodRow) => Promise<unknown>;
}

type PeriodAction = 'lock' | 'ceased' | 'comparative';

/** Direct icon buttons, same convention as every other row-actions column in the app. */
function PeriodActionSelect({
  element,
  canDelete,
  onEdit,
  onDelete,
  onLock,
  onCeased,
  onComparative,
}: PeriodActionsProps) {
  const [pending, setPending] = useState<PeriodAction | null>(null);

  const run = (action: PeriodAction, request: (row: PeriodRow) => Promise<unknown>) => {
    setPending(action);
    request(element).finally(() => setPending(null));
  };

  return (
    <div className="d-flex align-items-center grid-actions">
      <Button title="Edit" onClick={() => onEdit(element)}>
        <Icon name="edit" outlined />
      </Button>
      {canDelete && (
        <Button title="Delete" onClick={() => onDelete(element)}>
          <Icon name="delete_outline" outlined />
        </Button>
      )}
      <Button
        title={element.isLocked ? 'Unlock Period' : 'Lock Period'}
        disabled={pending === 'lock'}
        onClick={() => run('lock', onLock)}
      >
        <Icon name={element.isLocked ? 'lock' : 'lock_open'} outlined />
      </Button>
      <Button
        title={element.isCeased ? 'Continue' : 'Ceased'}
        disabled={pending === 'ceased'}
        onClick={() => run('ceased', onCeased)}
      >
        <Icon name={element.isCeased ? 'play_circle' : 'stop_circle'} outlined />
      </Button>
      <Button
        title={element.isComparative ? 'Undo Comparative' : 'Mark Comparative'}
        disabled={pending === 'comparative'}
        onClick={() => run('comparative', onComparative)}
      >
        <Icon name="compare_arrows" outlined />
      </Button>
    </div>
  );
}
